import { apiClient } from '@/api/client';
import type { PermissionKey } from '@/lib/constants/permissions';
import { ENTITY_TYPES, type EntityType } from '@/lib/constants/entity-types';

/** Membership role as returned by the backend (mirrors EntityMemberRole). */
export type MemberRole = 'owner' | 'admin' | 'sub_admin' | 'member';
/**
 * MEMBERSHIP status - is this person on the entity's team at all (active),
 * gone (revoked), or invited but not yet joined (pending)? Distinct from
 * `AccountStatus` below - do not conflate the two.
 */
export type MemberStatus = 'active' | 'revoked' | 'pending';
/**
 * USER ACCOUNT status - can this person's account sign in at all? Set by a
 * Super Admin from the admin users console (suspend/reinstate/delete), never
 * by this app. Orthogonal to `MemberStatus`: a suspended person can still
 * hold an 'active' team `status` row here - that mismatch is exactly what
 * `TeamMembersTable` reads this field to surface (MYN-133/135).
 */
export type AccountStatus = 'active' | 'suspended' | 'deactivated' | 'deleted';

/**
 * Sector / area of work recorded on an Investor member or invite (MYN-202,
 * FR-174); null for every other entity type and for an investor with none.
 */
export interface TeamSector {
  id: number;
  name: string;
}

/** A row of the Team Members table (mirrors backend TeamMemberView). */
export interface TeamMember {
  membershipId: string;
  userId: string;
  name: string;
  email: string;
  role: MemberRole;
  /** Membership status (active/revoked/pending) - see `MemberStatus`. */
  status: MemberStatus;
  /** Account status (active/suspended/deactivated/deleted) - see `AccountStatus`. */
  accountStatus: AccountStatus;
  isOwner: boolean;
  isSelf: boolean;
  permissions: {
    /** "Edit Profile / Perk" for a partner - MYN-181 merged offers.manage into this grant. */
    entityProfileEdit: boolean;
    teamInvite: boolean;
  };
  /**
   * Sector / area of work copied from the accepted invite (MYN-202); null when
   * none. Optional only so existing fixtures and older Backend builds stay valid.
   */
  sector?: TeamSector | null;
}

/** A pending/expired invite row (mirrors backend InviteView). */
export interface TeamInvite {
  id: string;
  email: string;
  /** The invitee's full name, if the inviter provided one; null for legacy pre-migration rows. */
  inviteeName: string | null;
  role: MemberRole;
  status: 'pending' | 'expired';
  expiresAt: string | null;
  /** Sector / area of work chosen on the invite (MYN-202); null when none. */
  sector?: TeamSector | null;
}

/** Shape the flat `/invites` resource actually returns (see Backend InvitesService.InviteSummary). */
interface InviteSummaryResponse {
  id: string;
  recipientEmail: string;
  inviteeName: string | null;
  roleToAssign: MemberRole;
  expiresAt: string | null;
  status: 'pending' | 'expired';
  sector?: TeamSector | null;
}

function toTeamInvite(summary: InviteSummaryResponse): TeamInvite {
  return {
    id: summary.id,
    email: summary.recipientEmail,
    inviteeName: summary.inviteeName,
    role: summary.roleToAssign,
    status: summary.status,
    expiresAt: summary.expiresAt,
    // Older Backend builds omit the field; normalise to null so the UI has one "none" shape.
    sector: summary.sector ?? null,
  };
}

/**
 * Team management resource segment per entity type (mirrors
 * `ENTITY_PROFILE_ROUTE` in `routes.ts`) - `TeamService` backend-side is
 * entity-agnostic, but the API is split by resource type per the backend's
 * "never a polymorphic endpoint" rule, so the client must pick the right one.
 */
const ENTITY_TEAM_API_BASE: Record<EntityType, string> = {
  [ENTITY_TYPES.STARTUP]: '/startups',
  [ENTITY_TYPES.INVESTOR]: '/investors',
  [ENTITY_TYPES.TRUSTED_PARTNER]: '/partners',
};

const base = (entityId: string, entityType: EntityType) =>
  `${ENTITY_TEAM_API_BASE[entityType]}/${entityId}`;

/** List entity members with their effective permission flags. */
export async function listTeamMembers(
  entityId: string,
  entityType: EntityType,
): Promise<TeamMember[]> {
  const { data } = await apiClient.get<TeamMember[]>(`${base(entityId, entityType)}/team`);
  return data;
}

/**
 * List the caller's own entity's open invites (one flat, entity-agnostic
 * `/invites` resource - the entity is resolved server-side from the caller's
 * session, never passed by the client).
 */
export async function listInvites(): Promise<TeamInvite[]> {
  const { data } = await apiClient.get<InviteSummaryResponse[]>('/invites');
  return data.map(toTeamInvite);
}

/**
 * Create + send a team invite (domain-restricted server-side for a startup
 * entity; no restriction for other types - see Backend's
 * `entity-invite-validators.ts`). Always invites as a plain member - an admin
 * promotes via the separate role-change action.
 *
 * `sectorId` (MYN-202) is Investor-only: it is sent only when one was chosen,
 * so every other entity type's request body is unchanged.
 */
export interface CreateInviteInput {
  email: string;
  inviteeName: string;
  sectorId?: number;
}

export async function createInvite({
  email,
  inviteeName,
  sectorId,
}: CreateInviteInput): Promise<TeamInvite> {
  const { data } = await apiClient.post<InviteSummaryResponse>('/invites', {
    recipientEmail: email,
    inviteeName,
    roleToAssign: 'member',
    ...(sectorId !== undefined && { sectorId }),
  });
  return toTeamInvite(data);
}

/**
 * Revoke a pending invite (the UI's "Remove Member" action on an invite row).
 * No resend endpoint is called from the FE — to give someone access again an
 * admin removes the old invite and sends a new one.
 */
export async function revokeInvite(inviteId: string): Promise<void> {
  await apiClient.post(`/invites/${inviteId}/revoke`);
}

/** Grant or revoke a single member permission toggle. */
export async function updateMemberPermission(
  entityId: string,
  entityType: EntityType,
  membershipId: string,
  permissionKey: PermissionKey,
  isEnabled: boolean,
): Promise<TeamMember> {
  const { data } = await apiClient.patch<TeamMember>(
    `${base(entityId, entityType)}/members/${membershipId}/permissions`,
    { permissionKey, isEnabled },
  );
  return data;
}

/** Promote a member to admin or demote to member. */
export async function updateMemberRole(
  entityId: string,
  entityType: EntityType,
  membershipId: string,
  role: 'admin' | 'member',
): Promise<TeamMember> {
  const { data } = await apiClient.patch<TeamMember>(
    `${base(entityId, entityType)}/members/${membershipId}/role`,
    { role },
  );
  return data;
}

/** Remove a member (soft revoke). */
export async function removeMember(
  entityId: string,
  entityType: EntityType,
  membershipId: string,
): Promise<void> {
  await apiClient.delete(`${base(entityId, entityType)}/members/${membershipId}`);
}
